
import React, { useEffect, useState, useCallback } from "react";
import {
	Box, Table, Thead, Tbody, Tr, Th, Td, Spinner, Text, Flex, Button, useToast
} from "@chakra-ui/react";
import { TriangleUpIcon, TriangleDownIcon } from "@chakra-ui/icons";
import { useTranslation } from "react-i18next";
import OrdersFilter from "./components/Filters";
import fetchWithToken from "views/auth/signIn/axiosInstance";

export default function ReadyOrdersReport() {
	const { t } = useTranslation();
	const toast = useToast();
	// Filters
	const [filterClientName, setFilterClientName] = useState("");
	const [filterStatus, setFilterStatus] = useState("");
	const [dateFilter, setDateFilter] = useState("today");
	const [customRange, setCustomRange] = useState({ from: "", to: "" });
	const [branch, setBranch] = useState("all");
	const [ordersPerPage, setOrdersPerPage] = useState(10);
	const [currentPage, setCurrentPage] = useState(1);
	const [dateSortOrder, setDateSortOrder] = useState("desc");
	// Data
	const [orders, setOrders] = useState([]);
	const [totalPages, setTotalPages] = useState(1);
	const [totalResults, setTotalResults] = useState(0);
	const [loading, setLoading] = useState(false);

	// Company ID (from sessionStorage or context, but can be set by filter)
	const [filterCompanyId, setFilterCompanyId] = useState("");
	const companyId = filterCompanyId ;

	// Prepare API payload
	const getApiPayload = useCallback(() => {
		let from = "";
		let to = "";
		let payload = {
			type: "admin",
			page: currentPage,
			limit: ordersPerPage,
			company_id: companyId || undefined,
			branch_id: branch !== "all" ? branch : undefined,
			status: filterStatus || undefined,
			sort_by: dateSortOrder,
			sort_field: "delivery_date",
			client_name: filterClientName || undefined,
		};
		if (dateFilter === "today") {
			const d = new Date();
			from = to = d.toISOString().slice(0, 10);
			payload.date_filter = "today";
		} else if (dateFilter === "tomorrow") {
			const d = new Date();
			d.setDate(d.getDate() + 1);
			from = to = d.toISOString().slice(0, 10);
			payload.date_filter = "tomorrow";
		} else if (dateFilter === "after_tomorrow") {
			const d = new Date();
			d.setDate(d.getDate() + 2);
			from = to = d.toISOString().slice(0, 10);
			payload.date_filter = "after_tomorrow";
		} else if (dateFilter === "custom") {
			payload.date_filter = "custom";
			from = customRange.from;
			to = customRange.to;
			if (from) payload.from = from;
			if (to) payload.to = to;
		}
		return payload;
	}, [branch, companyId, currentPage, customRange.from, customRange.to, dateFilter, filterClientName, filterStatus, ordersPerPage, dateSortOrder]);

	// Fetch orders
	const fetchOrders = useCallback(async () => {
		setLoading(true);
		try {
			const payload = getApiPayload();
			const res = await fetchWithToken(
        `${process.env.REACT_APP_API_URL_Report}reports/orders/ready`,
				"POST",
				payload
			);
			if (res.status !== "success") throw new Error(res.message || "Failed to fetch orders");
			setOrders(res.data.data || []);
			setTotalPages(res.data.meta.pagination.last_page || 1);
			setTotalResults(res.data.meta.pagination.total || 0);
		} catch (error) {
			toast({
				title: t("Error"),
				description: error.message,
				status: "error",
				position: "top-right",
				duration: 5000,
				isClosable: true,
			});
			setOrders([]);
			setTotalPages(1);
			setTotalResults(0);
		} finally {
			setLoading(false);
		}
	}, [getApiPayload, t, toast]);


	// Refetch on filter/sort/page change
	useEffect(() => {
		// If custom is selected, only fetch if both from and to are set
		if (dateFilter === "custom") {
			if (!customRange.from || !customRange.to) return;
		}
		fetchOrders();
		// eslint-disable-next-line
	}, [getApiPayload, dateFilter, customRange.from, customRange.to]);

	// Handle sort click
	const handleSortDate = () => {
		setDateSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
		setCurrentPage(1);
	};

	// Handle filter change (reset page)
	const handleFilterChange = () => {
		setCurrentPage(1);
		fetchOrders();
	};

	// Pagination controls
	const handlePageChange = (page) => {
		setCurrentPage(page);
	};

		return (
			<Box
				p={4}
				sx={{
					animation: 'fadeInReport 0.6s',
					background: 'linear-gradient(135deg, #f8fafc 0%, #e0f2fe 100%)',
					borderRadius: 'lg',
					boxShadow: '0 4px 24px 0 rgba(66,153,225,0.10)',
					'@keyframes fadeInReport': {
						from: { opacity: 0, transform: 'translateY(30px)' },
						to: { opacity: 1, transform: 'translateY(0)' },
					},
				}}
			>
			<OrdersFilter
				dateFilter={dateFilter}
				setDateFilter={setDateFilter}
				branch={branch}
				setBranch={setBranch}
				companyId={companyId}
				setCompanyId={setFilterCompanyId}
				customRange={customRange}
				setCustomRange={setCustomRange}
				filterStatus={filterStatus}
				setFilterStatus={setFilterStatus}
				ordersPerPage={ordersPerPage}
				setOrdersPerPage={setOrdersPerPage}
				currentPage={currentPage}
				setCurrentPage={setCurrentPage}
				showServiceFilter={false}
				
			/>

			<Box
				mt={6}
				sx={{
					animation: 'fadeInTable 0.7s',
					'@keyframes fadeInTable': {
						from: { opacity: 0, transform: 'translateY(20px)' },
						to: { opacity: 1, transform: 'translateY(0)' },
					},
				}}
			>
				{loading ? (
					<Flex justify="center" align="center" minH="200px"><Spinner size="xl" /></Flex>
				) : orders.length === 0 ? (
					<Text textAlign="center" color="gray.500">{t("No orders found")}</Text>
				) : (
					<Table
						variant="striped"
						colorScheme="gray"
						sx={{
							borderRadius: 'md',
							overflow: 'hidden',
							boxShadow: '0 2px 8px 0 rgba(0,0,0,0.04)',
							background: 'white',
						}}
					>
						<Thead>
							<Tr backgroundColor="#3c31ce" transition="background 0.3s" textColor={"white"}>
								<Th color={"white"}>{t("Company Name")}</Th>
								<Th color={"white"}>{t("Order ID")}</Th>
								<Th color={"white"}>{t("Customer ID")}</Th>
								<Th color={"white"}>{t("Branch Name")}</Th>
								<Th color={"white"}>{t("Total Amount")}</Th>
								<Th
									color={"white"}
									cursor="pointer"
									onClick={handleSortDate}
								>
									{t("Delivery Date")}
									{dateSortOrder === "desc" ? (
										<TriangleDownIcon ml={2} />
									) : (
										<TriangleUpIcon ml={2} />
									)}
								</Th>
								<Th color={"white"}>{t("Status")}</Th>
							</Tr>
						</Thead>
						<Tbody>
							{orders.map((order, idx) => (
								<Tr key={order.order_id + idx}>
									<Td>{order.company_name}</Td>
									<Td>{order.order_id}</Td>
									<Td>{order.customer_id}</Td>
									<Td>{order.branch_name}</Td>
									<Td>{order.total_amount}</Td>
									<Td>{order.delivery_date}</Td>
									<Td>{order.status}</Td>
								</Tr>
							))}
						</Tbody>
					</Table>
				)}
			</Box>

			{/* Pagination */}
			{totalPages > 1 && (
				<Flex justify="center" align="center" mt={6} gap={2} wrap="wrap">
					<Button
						onClick={() => handlePageChange(1)}
						disabled={currentPage === 1}
						size="sm"
						variant="outline"
					>
						{t("First")}
					</Button>
					<Button
						onClick={() => handlePageChange(Math.max(currentPage - 1, 1))}
						disabled={currentPage === 1}
						size="sm"
						variant="outline"
					>
						{t("Previous")}
					</Button>
					{/* Page numbers with ellipsis */}
					{(() => {
						const delta = 2;
						const range = [];
						const rangeWithDots = [];
						for (let i = Math.max(2, currentPage - delta); i <= Math.min(totalPages - 1, currentPage + delta); i++) {
							range.push(i);
						}
						if (currentPage - delta > 2) {
							rangeWithDots.push(1, '...');
						} else {
							rangeWithDots.push(1);
						}
						rangeWithDots.push(...range);
						if (currentPage + delta < totalPages - 1) {
							rangeWithDots.push('...', totalPages);
						} else if (totalPages > 1) {
							rangeWithDots.push(totalPages);
						}
						return rangeWithDots.map((page, index) => {
							if (page === '...') {
								return (
									<Text key={`ellipsis-${index}`} mx={2} alignSelf="center" fontSize="sm" color="gray.500">...</Text>
								);
							}
							return (
								<Button
									key={page}
									onClick={() => handlePageChange(page)}
									variant={currentPage === page ? "solid" : "outline"}
									colorScheme={currentPage === page ? "cyan" : "gray"}
									size="sm"
									minW="40px"
									isDisabled={currentPage === page}
								>
									{page}
								</Button>
							);
						});
					})()}
					<Button
						onClick={() => handlePageChange(Math.min(currentPage + 1, totalPages))}
						disabled={currentPage === totalPages}
						size="sm"
						variant="outline"
					>
						{t("Next")}
					</Button>
					<Button
						onClick={() => handlePageChange(totalPages)}
						disabled={currentPage === totalPages}
						size="sm"
						variant="outline"
					>
						{t("Last")}
					</Button>
				</Flex>
			)}
			{/* Results info */}
			<Text mt={4} textAlign="center" fontSize="sm" color="gray.600">
				{t("Page")} {currentPage} {t("of")} {totalPages} - {t("Showing")} {(orders.length > 0) ? ((currentPage - 1) * ordersPerPage + 1) : 0} {t("to")} {((currentPage - 1) * ordersPerPage) + orders.length} {t("results")}
			</Text>
		</Box>
	);
}
